import * as binderIds from "../../enums/binderIds";
import * as filterNames from "../../enums/filterNames";
import * as storageKeyHelpers from "../storageKeyHelpers";
import cacheEnhancer from "./cacheEnhancer";
import instance from "./instance";
import isProfileBinder from "../../lib/binder/isProfileBinder";
import { safeParseDate } from "../parseDate";

function parseBinderData(rawBinderData) {
	return {
		...rawBinderData,
		alert_last_time: safeParseDate(rawBinderData.alert_last_time),
		alert_start_time: safeParseDate(rawBinderData.alert_start_time),
		create_time: safeParseDate(rawBinderData.create_time),
		observers: rawBinderData.observers || [],
		share_time: safeParseDate(rawBinderData.share_time),
		update_time: safeParseDate(rawBinderData.update_time),
		view_time: safeParseDate(rawBinderData.view_time),
	};
}

function parseHistoryObjectDate(rawHistoryObjectData) {
	return {
		...rawHistoryObjectData,
		event_time: safeParseDate(rawHistoryObjectData.event_time),
	};
}

export function catalogList(attributes, libraries, orders, start, limit, query, user_id, group_id) {
	return instance
		.request({
			url: "/binders/catalog",
			method: "POST",
			data: {
				attributes,
				libraries,
				orders,
				start,
				limit,
				query,
				user_id,
				group_id,
			},
		})
		.then(response => ({
			total: response.data.total,
			binders: response.data.binders.map(binder => parseBinderData(binder)),
		}));
}

export function catalogCount(attributes, libraries, query, userId, groupId) {
	const data = {
		attributes,
		libraries,
		query,
	};

	if (userId !== null && userId !== undefined) {
		data.user_id = userId;
	}
	if (groupId !== null && groupId !== undefined) {
		data.group_id = groupId;
	}

	return instance
		.request({
			url: "/binders/catalog_count",
			method: "POST",
			data,
		})
		.then(response => response.data);
}

function networkGetBinder(libraryName, binderType, binderId) {
	return instance
		.request({
			url: "/binders/get_binder",
			method: "post",
			data: {
				library_name: libraryName,
				binder_type: binderType,
				id: binderId,
			},
		})
		.then(response => parseBinderData(response.data));
}

export function getBinder(libraryName, binderType, binderId) {
	if (isProfileBinder(binderType)) {
		const key = storageKeyHelpers.getProfileBinderKey(libraryName, binderType, binderId);
		return cacheEnhancer(networkGetBinder, key, 3600, false)(libraryName, binderType, binderId);
	}
	return networkGetBinder(libraryName, binderType, binderId);
}

export function create(
	title,
	library_name,
	binderTabs,
	clientcode,
	description,
	content,
	message,
	bindertype,
) {
	return instance
		.request({
			url: "/binders/create",
			method: "POST",
			data: {
				clientcode,
				content,
				description,
				library_name,
				message,
				tabs: binderTabs,
				title,
				bindertype,
			},
		})
		.then(response => parseBinderData(response.data));
}

export function update(binder_id, binderTabs, title, clientcode, description, content, message) {
	return instance
		.request({
			url: "/binders/update",
			method: "POST",
			data: {
				binder_id,
				clientcode,
				content,
				description,
				message,
				tabs: binderTabs,
				title,
			},
		})
		.then(response => parseBinderData(response.data));
}

export function getHistory(binder_id) {
	return instance
		.request({
			url: "/binders/get_history",
			method: "POST",
			data: {
				binder_id,
			},
		})
		.then(response =>
			response.data.map(historyObject => parseHistoryObjectDate(historyObject)),
		);
}

export function setOwner(binder_id, new_owner_id) {
	return instance
		.request({
			url: "/binders/set_owner",
			method: "POST",
			data: {
				binder_id,
				new_owner_id,
			},
		})
		.then(() => {});
}

export function share(binder_id, user_ids, message, binder_type, library_name) {
	return instance
		.request({
			url: "/binders/share",
			method: "POST",
			data: {
				binder_id,
				user_ids,
				message,
				binder_type,
				library_name,
			},
		})
		.then(response => response.data);
}

export function unshare(binder_id, user_ids, binder_type, library_name) {
	return instance
		.request({
			url: "/binders/unshare",
			method: "POST",
			data: {
				binder_id,
				user_ids,
				binder_type,
				library_name,
			},
		})
		.then(response => response.data);
}

export function setBookmark(binder_type, binder_id, bookmark, library_name) {
	return instance
		.request({
			url: "/binders/set_bookmark",
			method: "POST",
			data: {
				binder_type,
				binder_id,
				bookmark,
				library_name,
			},
		})
		.then(() => {});
}

export function setAlert(user_id, binder_type, binder_id, alert_rate, alert_history, library_name) {
	return instance
		.request({
			url: "/binders/set_alert",
			method: "POST",
			data: {
				user_id,
				binder_type,
				binder_id,
				alert_rate,
				alert_history,
				library_name,
			},
		})
		.then(response => response.data);
}

export function subscribe(binder_id, user_ids) {
	return instance
		.request({
			url: "/binders/subscribe",
			method: "POST",
			data: {
				binder_id,
				user_ids,
			},
		})
		.then(() => {});
}

export function unsubscribe(binder_id, user_ids) {
	return instance
		.request({
			url: "/binders/unsubscribe",
			method: "POST",
			data: {
				binder_id,
				user_ids,
			},
		})
		.then(() => {});
}

export function deleteBinder(binder_id, binder_type, library_name) {
	return instance
		.request({
			url: "/binders/delete",
			method: "POST",
			data: {
				binder_id,
				binder_type,
				library_name,
			},
		})
		.then(() => {});
}

export function getReport(libraryName, reportName, criteria) {
	return instance
		.request({
			url: "/binders/get_report",
			method: "POST",
			data: {
				library_name: libraryName,
				report_name: reportName,
				criteria,
			},
		})
		.then(response => parseBinderData(response.data));
}

const ATTRIBUTES = [filterNames.OWNED];
const SORT_ORDER = [];
export function reassignOwnedBinders(userId, reassignUserId, reassignLibraryNames) {
	return catalogCount(ATTRIBUTES, reassignLibraryNames, null, userId)
		.then(count => {
			return count
				? catalogList(ATTRIBUTES, reassignLibraryNames, SORT_ORDER, 0, count, null, userId)
				: Promise.resolve({ binders: [], count: 0 });
		})
		.then(catalog =>
			Promise.map(catalog.binders, binder => setOwner(binder.id, reassignUserId), {
				concurrency: 10,
			}),
		);
}

export function getMultiProfile(libraryName, profileType, profileIds) {
	return instance
		.request({
			url: "/binders/get_multi_profile",
			method: "POST",
			data: {
				library_name: libraryName,
				profile_type: profileType,
				profile_ids: profileIds,
			},
		})
		.then(response => parseBinderData(response.data));
}

export function getProfileLandingTabs() {
	return instance
		.request({
			url: "/binders/get_profile_landing_tabs",
			method: "POST",
		})
		.then(response => response.data);
}

export function getDRP(libraryName, profileType, profileIds) {
	return instance
		.request({
			url: "/binders/get_drp",
			method: "POST",
			data: {
				library_name: libraryName,
				profile_type: profileType,
				profile_ids: profileIds,
			},
		})
		.then(response => {
			const binder = parseBinderData(response.data);
			return {
				...binder,
				id: binderIds.UNASSIGNED,
			};
		});
}

export function getSearchBinder(libraryName, searchMemo) {
	return instance
		.request({
			url: "/binders/get_search_binder",
			method: "POST",
			data: {
				library_name: libraryName,
				search_memo: searchMemo,
			},
		})
		.then(response => {
			const binder = parseBinderData(response.data);
			return {
				...binder,
				id: binderIds.UNASSIGNED,
			};
		});
}

export function printTableauTab(tab, userId) {
	return instance
		.request({
			url: "/binders/print_tableau_tab",
			method: "POST",
			data: {
				tab,
				format: "image",
				user_id: userId,
			},
		})
		.then(res => res.data);
}

export function createSnapshot(snapshotId) {
	return instance
		.request({
			url: "/binders/create_snapshot",
			method: "POST",
			data: {
				snapshot_id: snapshotId,
			},
		})
		.then(res => res.data);
}
